import { useState, useMemo } from 'react';
import { Flex, Text, IconButton, Dropdown, Popconfirm, PageCard, MarkupPreview, message, Pagination, iconWeight, Icon } from '@nula/core-ui';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { Library } from './library';
import { SelectionActions } from './selection-actions';
import { formatDate } from '@/utils/format-date';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './trash-library.module.css';

// ── Unified trash item type ─────────────────────────────────────────────────

interface TrashItem {
  id: string;
  title: string;
  kind: 'document' | 'canvas' | 'collection';
  updatedAt: string;
  snippet?: string;
  previewMarkup?: string | null;
  previewKind?: 'html' | 'svg' | null;
}

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;

// ── Trash card ──────────────────────────────────────────────────────────────

function TrashCard({
  item,
  onRestore,
  onDeletePermanently,
  checkboxNode,
}: {
  item: TrashItem;
  onRestore: (id: string) => void;
  onDeletePermanently: (id: string) => void;
  checkboxNode?: React.ReactNode;
}) {
  const title = item.title || 'Untitled';
  const isCollection = item.kind === 'collection';
  const isCanvas = item.kind === 'canvas';
  const typeIcon = getEntityKindMeta(item.kind).icon;
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <>
      <Popconfirm
        title="Delete forever?"
        description="This cannot be undone."
        okText="Delete Forever"
        okDanger
        open={confirmDelete}
        onConfirm={() => { setConfirmDelete(false); onDeletePermanently(item.id); }}
        onCancel={() => setConfirmDelete(false)}
      >
        <span />
      </Popconfirm>
      <PageCard
        preview={
          item.previewMarkup ? (
            <MarkupPreview key={item.updatedAt} markup={item.previewMarkup} />
          ) : (
            <Flex align="center" justify="center" className={styles.emptyPreview}>
              <Text size="xs" className={styles.emptyPreviewLabel}>
                {isCollection ? 'Collection' : isCanvas ? 'Visual canvas' : 'Empty document'}
              </Text>
            </Flex>
          )
        }
        title={title}
        meta={formatDate(item.updatedAt)}
        icon={
          <Icon name={typeIcon} size={14} weight={iconWeight.light} />
        }
        actionsLeft={checkboxNode}
        actions={
          <Dropdown
            trigger={['click']}
            placement="bottom-end"
            menu={{
              items: [
                {
                  key: 'restore',
                  label: 'Restore',
                  icon: <Icon name="restore" size={14} weight={iconWeight.light} />,
                  onClick: () => onRestore(item.id),
                },
                { key: 'd1', label: '', type: 'divider' as const },
                {
                  key: 'delete',
                  label: 'Delete Forever',
                  icon: <Icon name="delete_forever" size={14} weight={iconWeight.light} />,
                  danger: true,
                  onClick: () => setConfirmDelete(true),
                },
              ],
            }}
          >
            <IconButton size="sm" aria-label="More options" className={styles.moreIconBtn}>
              <Icon name="more_horiz" size={16} weight={iconWeight.light} />
            </IconButton>
          </Dropdown>
        }
      />
    </>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function TrashLibrary() {
  const service = useWorkspaceService();
  const allDocs = useWorkspaceDocs();
  const allCollections = useWorkspaceCollections();

  const archivedDocuments = useMemo(() => allDocs.filter((d) => d.isArchived), [allDocs]);
  const archivedCollections = useMemo(() => allCollections.filter((c) => c.isArchived), [allCollections]);

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // Merge archived documents and collections into a unified list
  const allTrashItems: TrashItem[] = useMemo(() => {
    const docItems: TrashItem[] = archivedDocuments.map((d) => ({
      id: d.id,
      title: d.title,
      kind: d.kind,
      updatedAt: d.updatedAt,
      snippet: d.snippet,
      previewMarkup: d.previewMarkup,
      previewKind: d.previewKind,
    }));
    const colItems: TrashItem[] = archivedCollections.map((c) => ({
      id: c.id,
      title: c.name,
      kind: 'collection' as const,
      updatedAt: c.updatedAt,
    }));
    return [...docItems, ...colItems].sort((a, b) =>
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    );
  }, [archivedDocuments, archivedCollections]);

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allTrashItems;
    const q = searchQuery.toLowerCase();
    return allTrashItems.filter((d) => (d.title || '').toLowerCase().includes(q));
  }, [allTrashItems, searchQuery]);

  const currentPage = Math.min(page, Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE)));
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleRestore = (id: string) => {
    const item = allTrashItems.find((i) => i.id === id);
    if (item?.kind === 'collection') {
      service.archiveCollection(id, false);
      message.success('Collection restored');
    } else {
      service.archiveDoc(id, false);
      message.success('Document restored');
    }
  };

  const handleDeletePermanently = (id: string) => {
    const item = allTrashItems.find((i) => i.id === id);
    if (item?.kind === 'collection') {
      service.deleteCollectionPermanently(id).catch(() => {});
      message.success('Collection permanently deleted');
    } else {
      service.deleteDocPermanently(id).catch(() => {});
      message.success('Permanently deleted');
    }
  };

  const handleRestoreSelected = () => {
    selectedRowKeys.forEach((id) => handleRestore(id));
    message.success(`${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? 's' : ''} restored`);
    setSelectedRowKeys([]);
  };

  const handleDeleteSelected = () => {
    selectedRowKeys.forEach((id) => handleDeletePermanently(id));
    message.success(`${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? 's' : ''} permanently deleted`);
    setSelectedRowKeys([]);
  };

  return (
    <Library
      title="Recently Deleted"
      heroIcon="delete"
      items={pagedItems}
      cardLayout="pageGrid"
      renderCard={(item, _index, _vm, checkboxNode) => (
        <TrashCard
          item={item}
          onRestore={handleRestore}
          onDeletePermanently={handleDeletePermanently}
          checkboxNode={checkboxNode}
        />
      )}
      keyExtractor={(item) => item.id}
      searchPlaceholder="Search trash..."
      onSearch={handleSearch}
      emptyIllustration="trash"
      emptyTitle="The void is empty"
      emptyMessage="There are no discarded items here. Your sanctuary is perfectly balanced."
      selectedIds={new Set(selectedRowKeys)}
      onToggleSelect={(id) => setSelectedRowKeys((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id])}
      onSelectAll={(ids) => setSelectedRowKeys(ids)}
      onClearSelection={() => setSelectedRowKeys([])}
      selectionActions={
        selectedRowKeys.length > 0 ? (
          <SelectionActions
            actions={[
              { key: 'restore', icon: 'restore', label: 'Restore', onClick: handleRestoreSelected },
              {
                key: 'delete',
                icon: 'delete_forever',
                label: 'Delete Forever',
                onClick: handleDeleteSelected,
                danger: true,
                confirm: `Permanently delete ${selectedRowKeys.length} item${selectedRowKeys.length > 1 ? 's' : ''}?`,
              },
            ]}
          />
        ) : undefined
      }
      pagination={
        filteredItems.length > PAGE_SIZE ? (
          <Flex justify="center" className={styles.paginationRow}>
            <Pagination
              current={currentPage}
              total={filteredItems.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </Flex>
        ) : undefined
      }
    />
  );
}
